import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import StatusPill from '../../components/StatusPill';
import FormError from '../../components/FormError';

type Campaign = {
  id: string; name: string; text: string | null; total: number; sent: number; failed: number;
  status: string; created_at: string;
};
type Lead = { id: string; email: string; company_name: string };
type Seller = { id: string; fullName: string };

export default function Campanhas() {
  const [list, setList] = useState<Campaign[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [sel, setSel] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [text, setText] = useState('Olá {{empresa}}! Aqui é da Meta Telecom...');
  const [cadence, setCadence] = useState(30);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [c, l] = await Promise.all([
      api.get<{ campaigns: Campaign[] }>('/campaigns'),
      api.get<{ leads: Lead[] }>('/leads'),
    ]);
    setList(c.campaigns);
    setLeads(l.leads);
  }, []);
  useEffect(() => { load(); }, [load]);

  function toggle(id: string) {
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  }

  async function disparar() {
    if (!sel.length) { setFormError('Selecione os leads para o disparo.'); return; }
    setBusy(true);
    setFormError(null);
    try {
      const r = await api.post<{ campaignId: string; total: number }>('/campaigns/run', { name: name || 'Campanha', text, cadenceSeconds: cadence, leadIds: sel });
      toast.success(`Campanha criada: ${r.total} leads na fila (cadência ${cadence}s).`);
      await load();
    } catch (err) {
      const e = err as { message?: string; details?: Record<string, string[]> };
      const first = e.details ? Object.entries(e.details)[0] : null;
      setFormError(first ? `${first[0]}: ${first[1][0]}` : e.message ?? 'Falha no disparo');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
      <section>
        <header>
          <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">Campanhas (disparo)</p>
          <h1 className="mt-1 text-xl font-semibold tracking-tight">Disparo por WhatsApp</h1>
          <p className="mt-1 text-sm text-[var(--text-dim)]">Separado do Atendimento: aqui você dispara a um lote de leads; as respostas ficam no Atendimento.</p>
        </header>
        <form onSubmit={(e) => { e.preventDefault(); void disparar(); }} className="mt-4 space-y-3 rounded-lg border border-[var(--border)] bg-white p-4">
          <div>
            <label htmlFor="c-name" className="mb-1 block text-sm font-medium">Nome da campanha</label>
            <input id="c-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Ofertas de Outubro" className="w-full h-10 rounded-lg border border-[var(--border-strong)] bg-white px-3 text-sm" />
          </div>
          <div>
            <label htmlFor="c-text" className="mb-1 block text-sm font-medium">Mensagem</label>
            <textarea id="c-text" value={text} onChange={(e) => setText(e.target.value)} rows={5} className="w-full rounded-lg border border-[var(--border-strong)] bg-white p-3 text-sm" />
            <p className="mt-0.5 text-[11px] text-[var(--text-dim)]">{'Escreva {empresa} onde deva aparecer o nome da empresa do lead.'}</p>
          </div>
          <div>
            <label htmlFor="c-cad" className="mb-1 block text-sm font-medium">Cadência (segundos entre envios)</label>
            <input id="c-cad" type="number" min={15} max={600} value={cadence} onChange={(e) => setCadence(Number(e.target.value))} className="w-40 h-10 rounded-lg border border-[var(--border-strong)] bg-white px-3 text-sm" />
            <p className="mt-0.5 text-[11px] text-[var(--text-dim)]">Respeite cadências altas (60s+) para proteger o número.</p>
          </div>
          <button type="submit" disabled={busy} className="h-10 rounded-lg bg-[var(--accent)] px-5 text-sm font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">Disparar</button>
          <FormError error={formError} onClear={() => setFormError(null)} />
        </form>
      </section>

      <aside className="space-y-4">
        <section className="rounded-lg border border-[var(--border)] bg-white p-4">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-[var(--text-dim)]">Selecione os leads</h2>
          <ul className="mt-2 max-h-[46vh] overflow-y-auto text-sm">
            {leads.map((l) => (
              <li key={l.id} className="border-b border-[var(--border)] py-1 last:border-0">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={sel.includes(l.id)} onChange={() => toggle(l.id)} />
                  <span className="truncate">{l.company_name}</span>
                </label>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-[var(--text-dim)]">{sel.length} selecionado(s)</p>
        </section>
        <section className="rounded-lg border border-[var(--border)] bg-white p-4 text-sm">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-[var(--text-dim)]">Disparos recentes</h2>
          <ul className="mt-2 space-y-1.5">
            {list.map((c) => (
              <li key={c.id} className="border-b border-[var(--border)] pb-1 last:border-0">
                <span className="block font-medium">{c.name}</span>
                <span className="text-[11px] text-[var(--text-dim)]">
                  {c.sent}/{c.total} enviados{c.failed > 0 ? ` · ${c.failed} erros` : ''} · status {c.status}
                </span>
              </li>
            ))}
            {list.length === 0 && <li className="text-[11px] text-[var(--text-dim)]">Nenhum ainda.</li>}
          </ul>
        </section>
      </aside>
    </div>
  );
}
